import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Oekt } from "../state/session";
import * as su from "../state/su";
import { useLevende } from "../state/levende";

/**
 * Redaktørens flate for SU- og synkspørsmål.
 *
 * HELE PRODUKSJONEN PÅ ÉN GANG. Første utgave hadde en liste til venstre og
 * én persons spørsmål til høyre, og det var feil form: redaktøren skriver
 * ikke til én deltaker, hen legger en plan for alle. Med én person framme om
 * gangen er «oversikten» redusert til et tall, og man må klikke seg gjennom
 * lista for å se om noen mangler noe.
 *
 * Nå ligger alle som kort i et rutenett. Da leses hele synkplanen i ett
 * blikk, og det finnes ingenting å bytte mellom — den raskeste måten å
 * skifte deltaker på er å slippe å gjøre det.
 *
 * KORTET ER BRETTET, LISTA ER ARKET. Navnet og antallet står på kortet, og
 * spørsmålene ligger oppå som et lysere ark med egen kant. Uten den
 * delingen fløt lista sammen med kortet, og man så ikke hvor man kunne
 * skrive.
 *
 * SPØRSMÅLENE ER TEKST, IKKE FELTER. De står som linjer man kan lese, og
 * viser seg som redigerbare først når man peker på dem. Et rutenett av
 * rammer leses som et skjema som skal fylles ut, og dette er ikke et skjema
 * — det er noe som skrives.
 *
 * TIL ALLE FØRST. Generelle spørsmål hører ikke til noen bestemt og stilles
 * til hver eneste deltaker. Kortet ligger først, i tidskodefargen, fordi ett
 * spørsmål skrevet der treffer alle — en annen handling enn å skrive til én.
 */

/** `deltakerId` for de generelle spørsmålene. */
const TIL_ALLE = null;


export function SpoersmaalScreen({ oekt }: { readonly oekt: Oekt }): JSX.Element {
  const produksjonId = oekt.production.id;
  const [data, setData] = useState<su.SuData>(su.TOM);
  const [laster, setLaster] = useState(true);
  /** Hvilket kort som har et halvskrevet nytt spørsmål. */
  const [utkast, setUtkast] = useState<{ id: string | null; tekst: string }>(
    { id: TIL_ALLE, tekst: "" },
  );
  const skriver = useRef(false);

  const last = useCallback((): void => {
    void (async () => {
      const d = await su.hent(produksjonId);
      setData(d);
      setLaster(false);
    })();
  }, [produksjonId]);

  useEffect(last, [last]);

  /* Redaktøren er sjelden alene om å legge planen. */
  useLevende("spoersmaal", ["su_question", "participant"], last);

  const perDeltaker = useMemo(() => {
    const m = new Map<string | null, su.Spoersmaal[]>();
    m.set(TIL_ALLE, []);
    for (const d of data.deltakere) m.set(d.id, []);
    for (const q of data.spoersmaal) {
      const liste = m.get(q.deltakerId);
      if (liste !== undefined) liste.push(q);
    }
    return m;
  }, [data]);

  const tomme = useMemo(
    () => data.deltakere.filter((d) => (perDeltaker.get(d.id)?.length ?? 0) === 0).length,
    [data.deltakere, perDeltaker],
  );

  const leggTil = useCallback(async (deltakerId: string | null): Promise<void> => {
    if (skriver.current) return;
    const tekst = utkast.tekst.trim();
    if (tekst.length === 0 || utkast.id !== deltakerId) return;
    skriver.current = true;
    setUtkast({ id: deltakerId, tekst: "" });
    const antall = perDeltaker.get(deltakerId)?.length ?? 0;
    const q = await su.leggTil(produksjonId, deltakerId, tekst, antall, oekt.shootDay);
    skriver.current = false;
    if (q === null) {
      // Teksten kommer tilbake. Ingenting skal gå tapt fordi nettet svikter.
      setUtkast({ id: deltakerId, tekst });
      return;
    }
    setData((prev) => ({ ...prev, spoersmaal: [...prev.spoersmaal, q] }));
  }, [utkast, produksjonId, perDeltaker, oekt.shootDay]);

  const endre = useCallback(async (id: string, tekst: string): Promise<void> => {
    const t = tekst.trim();
    const foer = data.spoersmaal.find((q) => q.id === id);
    if (foer === undefined || foer.tekst === t || t.length === 0) return;
    setData((prev) => ({
      ...prev,
      spoersmaal: prev.spoersmaal.map((q) => (q.id === id ? { ...q, tekst: t } : q)),
    }));
    if (!await su.endre(id, t)) {
      setData((prev) => ({
        ...prev,
        spoersmaal: prev.spoersmaal.map((q) => (q.id === id ? foer : q)),
      }));
    }
  }, [data.spoersmaal]);

  const fjern = useCallback(async (id: string): Promise<void> => {
    const foer = data.spoersmaal;
    setData((prev) => ({ ...prev, spoersmaal: prev.spoersmaal.filter((q) => q.id !== id) }));
    if (!await su.fjern(id)) setData((prev) => ({ ...prev, spoersmaal: foer }));
  }, [data.spoersmaal]);

  /*
   * Samme skall som opplastingsflata: `.flate` fyller sida, og
   * `.flate__innhold` er det som ruller.
   */
  const skall = (innhold: JSX.Element): JSX.Element => (
    <div className="flate">
      <div className="flate__innhold">{innhold}</div>
    </div>
  );

  if (laster) {
    return skall(<p className="synk__melding">Henter spørsmålene …</p>);
  }

  /* Ikke en blank flate: uten deltakere er det ingenting å skrive til, og
     teksten må si hvorfor. */
  if (data.deltakere.length === 0) {
    return skall(
      <div className="synk__melding">
        <strong>Ingen deltakere i produksjonen.</strong>
        <span>
          Deltakerne legges inn med forkortelse — ALI for Alina — og det er
          forkortelsen regi skriver i loggen.
        </span>
      </div>,
    );
  }

  const kort = (
    id: string | null, merke: string, navn: string,
  ): JSX.Element => {
    const mine = perDeltaker.get(id) ?? [];
    const tomt = mine.length === 0;
    return (
      <section
        key={id ?? "alle"}
        className={[
          "synkkort",
          id === TIL_ALLE ? "synkkort--alle" : "",
          tomt ? "synkkort--tomt" : "",
        ].filter(Boolean).join(" ")}
        aria-label={`Spørsmål til ${navn}`}
      >
        <header className="synkkort__hode">
          <span className="synkkort__merke mono">{merke}</span>
          <span className="synkkort__navn">{navn}</span>
          <span className="synkkort__antall mono">{mine.length}</span>
        </header>

        {/* Arket: lista og linja man skriver et nytt spoersmaal paa. Uten
            den delingen floet innholdet sammen med kortet. */}
        <div className="synkkort__ark">
          <ul className="synkkort__liste">
          {mine.map((q) => (
            <li key={q.id} className="synkkort__linje">
              <input
                className="synkkort__tekst"
                defaultValue={q.tekst}
                onBlur={(e) => void endre(q.id, e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") e.currentTarget.blur();
                  if (e.key === "Escape") {
                    e.currentTarget.value = q.tekst;
                    e.currentTarget.blur();
                  }
                }}
                aria-label="Spørsmål"
              />
              <button
                type="button"
                className="synkkort__fjern"
                onClick={() => void fjern(q.id)}
                aria-label={`Fjern spørsmålet «${q.tekst}»`}
              >
                ×
              </button>
            </li>
          ))}
        </ul>

        <input
          className="synkkort__nytt"
          value={utkast.id === id ? utkast.tekst : ""}
          onChange={(e) => setUtkast({ id, tekst: e.target.value })}
          onFocus={() => setUtkast((p) => (p.id === id ? p : { id, tekst: "" }))}
          onKeyDown={(e) => { if (e.key === "Enter") void leggTil(id); }}
          onBlur={() => void leggTil(id)}
          placeholder={tomt ? "Skriv det første spørsmålet" : "Nytt spørsmål"}
          aria-label={`Nytt spørsmål til ${navn}`}
        />
        </div>
      </section>
    );
  };

  return skall(
    <div className="synk">
      {/*
        Én linje med summene. Den sier om planen er i rute før man leser et
        eneste kort — og «uten spørsmål» er tallet redaktøren er her for.
      */}
      <p className="synk__sum">
        <span>{data.deltakere.length} deltakere</span>
        <span className="synk__sum-skille" aria-hidden="true">·</span>
        <span>{data.spoersmaal.length} spørsmål</span>
        {tomme > 0 ? (
          <>
            <span className="synk__sum-skille" aria-hidden="true">·</span>
            <span className="synk__sum-mangler">
              {tomme === 1 ? "1 uten spørsmål" : `${tomme} uten spørsmål`}
            </span>
          </>
        ) : null}
      </p>

      <div className="synk__bord">
        {/* Navnet sier hva som SKJER, ikke hvem kortet heter: et spørsmål
            skrevet her dukker opp øverst på hver eneste deltakers liste. */}
        {kort(TIL_ALLE, "ALLE", "Stilles til alle")}
        {data.deltakere.map((d) => kort(d.id, d.forkortelse, d.navn))}
      </div>
    </div>,
  );
}
